import type { AppMeta, HabitRecord, PlanRecord, PlanStateRecord, UserSettings } from './types.js';
import { habits, plans, planStates, settings, metaAll, ensureInstall } from '../storage/db.js';

export interface AppState {
  meta: AppMeta;
  settings: UserSettings;
  /** All habits (active and archived). */
  habits: HabitRecord[];
  /** All plans. */
  plans: PlanRecord[];
  /** All plan states. */
  planStates: PlanStateRecord[];
  /** Default habit (meta.active_habit_id if set, else first active). */
  activeHabit?: HabitRecord | undefined;
  /** Default plan tied to default habit. */
  activePlan?: PlanRecord | undefined;
  activePlanState?: PlanStateRecord | undefined;
}

let _state: AppState | null = null;
const subs = new Set<() => void>();

export async function loadState(): Promise<AppState> {
  await ensureInstall();
  const meta = (await metaAll()) as AppMeta;
  const s = await settings.get();
  const allHabits = await habits.all();
  const allPlans = await plans.all();
  const allStates = await planStates.all();

  let activeHabit: HabitRecord | undefined;
  if (meta.active_habit_id) activeHabit = allHabits.find((h) => h.id === meta.active_habit_id);
  if (!activeHabit) activeHabit = allHabits.find((h) => h.active) ?? allHabits[0];

  let activePlan: PlanRecord | undefined;
  let activePlanState: PlanStateRecord | undefined;
  if (activeHabit) {
    const planList = allPlans.filter((p) => p.habitId === activeHabit!.id);
    activePlan = planList.find((p) => p.active) ?? planList[0];
    if (activePlan) activePlanState = allStates.find((ps) => ps.planId === activePlan!.id);
  }
  _state = { meta, settings: s, habits: allHabits, plans: allPlans, planStates: allStates, activeHabit, activePlan, activePlanState };
  return _state;
}

export function activeHabits(state = getState()): HabitRecord[] {
  return state.habits.filter((h) => h.active);
}

export function planFor(habitId: string, state = getState()): { plan?: PlanRecord | undefined; planState?: PlanStateRecord | undefined } {
  const plan = state.plans.find((p) => p.habitId === habitId && p.active) ?? state.plans.find((p) => p.habitId === habitId);
  const planState = plan ? state.planStates.find((ps) => ps.planId === plan.id) : undefined;
  return { plan, planState };
}

export function getState(): AppState {
  if (!_state) throw new Error('state not loaded');
  return _state;
}

export async function refresh(): Promise<AppState> {
  const next = await loadState();
  for (const fn of subs) fn();
  return next;
}

export function onChange(fn: () => void) { subs.add(fn); return () => subs.delete(fn); }
